import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { useMutation } from "convex/react";

import { TransientStatusBanner } from "../components/feedback/TransientStatusBanner";
import { isKnownTabPath, KeepAliveTabPanels } from "../components/navigation/KeepAliveTabPanels";
import { LiquidGlassTabBar } from "../components/navigation/LiquidGlassTabBar";
import { PremiumTabGateFlow } from "../features/monetization/PremiumTabGateFlow";
import { HabitsTab } from "../features/habits/HabitsTab";
import { InsightTab } from "../features/insight/InsightTab";
import { OnboardingFlow } from "../features/onboarding/OnboardingFlow";
import { readOnboardingCompleted } from "../features/onboarding/onboardingStorage";
import { DeleteDataPage } from "../pages/legal/DeleteDataPage";
import { PrivacyPolicyPage } from "../pages/legal/PrivacyPolicyPage";
import { TermsPage } from "../pages/legal/TermsPage";
import { isPublicLegalPath } from "../pages/legal/legalRoutes";
import { OnboardingAnalyticsDashboardPage } from "../pages/internal/OnboardingAnalyticsDashboardPage";
import { ONBOARDING_ANALYTICS_DASHBOARD_PATH } from "@/lib/env";
import { privacyUrl, termsUrl } from "@/lib/env";
import { RulesTab } from "../features/rules/RulesTab";
import { SwipeToUnlockBar } from "../features/rules/SwipeToUnlockBar";
import { SettingsTab } from "../features/settings/SettingsTab";
import { TodayTab } from "../features/today/TodayTab";
import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";
import { mutationRef } from "../lib/convex/refs";
import { useDeviceId, useDeviceReady } from "../lib/device/DeviceProvider";
import { useIsMarketingDemoActive } from "../lib/marketing/MarketingDemoProvider";
import { UsagePermissionGate } from "../components/usage/UsagePermissionGate";
import { isPremiumTabPath, type PremiumTabPath } from "@/lib/monetization/premiumTabs";
import { usePremiumAccess } from "@/lib/purchases/usePremiumAccess";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { collectTodayUsage, collectUsageForDay, snapshotPermissionFromNative, unavailableUsageSnapshot } from "../lib/native/usage";
import { resolveClientPlatform } from "../lib/native/syncUsagePlatform";
import { runUsageHistorySync } from "../lib/native/usageHistorySync";
import { configurePurchases } from "../lib/purchases/revenuecat";
import { isIosUsagePermissionGateActive, useUsageAccess } from "../lib/native/usageAccess";
import type { UsageSnapshotInput } from "../lib/native/plugins";

function usageDiagnosticsPayload(usage: UsageSnapshotInput) {
  return {
    reason: usage.reason,
    permission: usage.permission,
    totalSeconds: usage.totalSeconds,
    topAppCount: usage.topApps.length,
    extensionDiagnostic: usage.extensionDiagnostic,
    nativeDiagnostic: usage.nativeDiagnostic,
    pipelineSummary: usage.pipelineSummary,
    captureSource: usage.captureSource,
    waitedMs: usage.waitedMs,
    mountedReport: usage.mountedReport,
    extensionEmbedded: usage.extensionEmbedded,
    collectDurationMs: usage.collectDurationMs,
  };
}

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const anonymousDeviceId = useDeviceId();
  const marketingDemoActive = useIsMarketingDemoActive();
  const { reinstallRulesNotice, dismissReinstallRulesNotice } = useDeviceReady();
  const { permission, permissionReady, refreshPermission } = useUsageAccess();
  const { isPremium, loading: premiumLoading, refresh: refreshPremium, grantDevPremium, acknowledgePremiumUnlock } = usePremiumAccess();
  const gateActive = isIosUsagePermissionGateActive(permissionReady, permission);
  const onSettings = location.pathname === "/settings";
  const [syncError, setSyncError] = useState<string | null>(null);
  const [onboardingCompleted, setOnboardingCompletedState] = useState(readOnboardingCompleted);
  const syncUsage = useMutation(mutationRef<{ anonymousDeviceId: string; platform?: "ios" | "android" | "web"; snapshot: UsageSnapshotInput }, { id: string }>("usage:syncUsage"));

  const premiumTabRequested =
    onboardingCompleted && !premiumLoading && !isPremium && isPremiumTabPath(location.pathname);
  const requestedPremiumTab: PremiumTabPath | null = premiumTabRequested
    ? (location.pathname as PremiumTabPath)
    : null;

  const handleOnboardingComplete = useCallback(() => {
    setOnboardingCompletedState(true);
  }, []);

  const handleGateDismiss = useCallback(() => {
    navigate("/today", { replace: true });
  }, [navigate]);

  const handleGateUnlock = useCallback(async () => {
    if (import.meta.env.DEV) {
      grantDevPremium();
    } else {
      await acknowledgePremiumUnlock();
    }
    await refreshPremium();
    // Stay on the tab the user originally requested (location.pathname).
  }, [acknowledgePremiumUnlock, grantDevPremium, refreshPremium]);

  useEffect(() => {
    document.documentElement.dataset.screen0NativeStartup = "ready";
  }, []);

  // Initialize RevenueCat once at startup (native only) with the anonymous device id.
  useEffect(() => {
    void configurePurchases(anonymousDeviceId);
  }, [anonymousDeviceId]);

  const runUsagePipeline = useCallback(async () => {
    const clientPlatform = resolveClientPlatform();
    let usage: UsageSnapshotInput;
    try {
      usage = await collectTodayUsage();
    } catch (error) {
      console.error("Native usage collection failed", error);
      usage = {
        ...unavailableUsageSnapshot("unavailable", "plugin_error"),
        collectDurationMs: 0,
      };
    }

    const permission = await refreshPermission();
    if (permission.status === "granted" && usage.permission !== "granted") {
      usage = { ...usage, permission: "granted" };
    } else if (permission.status !== "granted" && usage.permission === "granted") {
      usage = { ...usage, permission: snapshotPermissionFromNative(permission.status) };
    }

    if (marketingDemoActive) {
      console.info("Screen0 usage sync skipped — marketing demo mode active");
      return usage;
    }

    try {
      await syncUsage({ anonymousDeviceId, platform: clientPlatform, snapshot: usage });
      setSyncError(null);
      console.info("Screen0 usage synced", usageDiagnosticsPayload(usage));
    } catch (error) {
      console.error("Screen0 usage sync failed", error);
      setSyncError(t.app.syncError);
    }

    if (usage.permission === "granted") {
      void runUsageHistorySync(
        collectUsageForDay,
        (snapshot) => syncUsage({ anonymousDeviceId, platform: clientPlatform, snapshot }),
      ).catch((error) => {
        console.error("Screen0 usage history sync failed", error);
      });
    }

    return usage;
  }, [anonymousDeviceId, marketingDemoActive, refreshPermission, syncUsage, t.app.syncError]);

  const didEagerCollectRef = useRef(false);
  useEffect(() => {
    if (didEagerCollectRef.current) return;
    didEagerCollectRef.current = true;
    void runUsagePipeline().catch(() => {});
  }, [runUsagePipeline]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const listener = CapacitorApp.addListener("appStateChange", (event) => {
      if (event.isActive) {
        void runUsagePipeline().catch(() => {});
      }
    });
    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [runUsagePipeline]);

  const bottomBannerOffsetClass = gateActive
    ? "bottom-[max(18px,env(safe-area-inset-bottom))]"
    : "bottom-[calc(96px+env(safe-area-inset-bottom))]";

  const reinstallRulesBanner = reinstallRulesNotice ? (
    <div className={`fixed inset-x-4 z-40 ${bottomBannerOffsetClass}`}>
      <TransientStatusBanner
        message={t.app.reinstallRulesPurged}
        variant="info"
        onDismiss={dismissReinstallRulesNotice}
        dismissLabel={t.common.dismiss}
        className="shadow-lg"
      />
    </div>
  ) : null;

  if (isPublicLegalPath(location.pathname)) {
    if (location.pathname === "/legal/delete-data") {
      return <DeleteDataPage />;
    }
    if (location.pathname === "/legal/terms" || location.pathname === termsUrl) {
      return <TermsPage />;
    }
    if (location.pathname === "/legal/privacy" || location.pathname === privacyUrl) {
      return <PrivacyPolicyPage />;
    }
  }

  if (location.pathname === ONBOARDING_ANALYTICS_DASHBOARD_PATH) {
    return <OnboardingAnalyticsDashboardPage />;
  }

  if (!onboardingCompleted) {
    return (
      <>
        <OnboardingFlow onComplete={handleOnboardingComplete} />
        {reinstallRulesBanner}
      </>
    );
  }

  if (location.pathname === "/" || !isKnownTabPath(location.pathname)) {
    return <Navigate to="/today" replace />;
  }

  if (onboardingCompleted && premiumLoading && isPremiumTabPath(location.pathname)) {
    return <Navigate to="/today" replace />;
  }

  if (gateActive && location.pathname === "/rules") {
    return <Navigate to="/today" replace />;
  }

  if (requestedPremiumTab) {
    return (
      <PremiumTabGateFlow
        requestedTab={requestedPremiumTab}
        onDismiss={handleGateDismiss}
        onUnlock={() => void handleGateUnlock()}
      />
    );
  }

  const syncErrorBanner = syncError ? (
    <div
      className={`liquid-glass-alert fixed inset-x-4 z-40 p-3 text-sm leading-6 text-[oklch(88%_0.025_35)] ${
        reinstallRulesNotice
          ? gateActive
            ? "bottom-[calc(72px+env(safe-area-inset-bottom))]"
            : "bottom-[calc(168px+env(safe-area-inset-bottom))]"
          : bottomBannerOffsetClass
      }`}
      role="alert"
    >
      <div className="flex items-start justify-between gap-3">
        <span>{syncError}</span>
        <button className="min-h-8 min-w-8 rounded-md text-xs font-semibold" onClick={() => setSyncError(null)}>{t.common.ok}</button>
      </div>
    </div>
  ) : null;

  if (gateActive && onSettings) {
    return (
      <>
        <SettingsTab onCollectUsage={runUsagePipeline} />
        {reinstallRulesBanner}
        {syncErrorBanner}
      </>
    );
  }

  if (gateActive) {
    return (
      <>
        <UsagePermissionGate onCollectUsage={runUsagePipeline} />
        {reinstallRulesBanner}
        {syncErrorBanner}
      </>
    );
  }

  const canAccessPremiumTabs = isPremium;

  return (
    <>
      <KeepAliveTabPanels
        panels={[
          { path: "/today", children: <TodayTab onCollectUsage={runUsagePipeline} /> },
          { path: "/habits", children: canAccessPremiumTabs ? <HabitsTab /> : null },
          { path: "/insight", children: canAccessPremiumTabs ? <InsightTab /> : null },
          { path: "/rules", children: canAccessPremiumTabs ? <RulesTab /> : null },
          { path: "/settings", children: <SettingsTab onCollectUsage={runUsagePipeline} /> },
        ]}
      />
      <LiquidGlassTabBar isPremium={isPremium} />
      <SwipeToUnlockBar />
      {reinstallRulesBanner}
      {syncErrorBanner}
    </>
  );
}
